/**
 * Emergent Marketplace — Phase 4
 * VendorAvailability.jsx
 * Architectural Layer: Presentation → Vendor Dashboard
 *
 * Practitioner configures:
 *   - Weekly availability schedule (days + time blocks)
 *   - Session types (title, duration, price)
 *   - Blocked/unavailable dates
 *   - Booking mode toggle (in-app / public / external)
 *   - Timezone selection
 *
 * Design: Structured calm — slate blue-grey, warm white, teal action color.
 * Font: DM Serif Display (headings) + Mulish (UI)
 */

import { useState, useEffect } from "react";
import {
  Clock, Plus, Trash2, Check, Globe, Calendar,
  ToggleLeft, ToggleRight, Save, AlertCircle, AlertTriangle, ChevronDown,
  ExternalLink, Loader, X, Copy, GripVertical, Mail
} from "lucide-react";
import { apiFetch } from "./api";
import IntakeFormsEditor from "@/components/marketplace/IntakeFormsEditor";
import SessionFollowupsEditor from "@/components/marketplace/SessionFollowupsEditor";
import BookingAccessNotice from "./BookingAccessNotice";
import ClientEmailsPanel from "./ClientEmailsPanel";
import SchedulingLimitsPanel from "./SchedulingLimitsPanel";
import { cssUrl } from "@/lib/cssUrl";

// The address clients will actually visit. On the app itself that is this origin; in a
// Capacitor build there is no web origin to borrow, so the configured web URL wins.
const WEB_ORIGIN = process.env.REACT_APP_WEB_URL || window.location.origin;

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Mulish:wght@300;400;600;700&display=swap');

  .vav-root {
    --slate-blue:  #2a3550;
    --slate-mid:   #3d5070;
    --teal:        #1a7a5a;
    --teal-light:  #2a9a74;
    --teal-bg:     #eaf7f2;
    --teal-border: #a0d8c4;
    --white:       #ffffff;
    --off-white:   #f6f8fb;
    --border:      #dde3ee;
    --ink:         #1a2030;
    --ink-mid:     #4a5570;
    --ink-light:   #8a96b0;
    --amber:       #9a6010;
    --amber-bg:    #fef9ea;
    --red:         #a02828;
    --red-bg:      #fdf0f0;
    font-family: 'Mulish', sans-serif;
    background: var(--off-white);
    color: var(--ink);
    min-height: 100vh;
    overflow-x: hidden;
  }

  .vav-header {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 20px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .vav-header h1 {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    color: var(--slate-blue);
    margin: 0 0 3px;
  }

  .vav-header p { font-size: 13px; color: var(--ink-light); margin: 0; }

  .vav-save-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 22px;
    border-radius: 10px;
    background: var(--teal);
    border: none;
    color: white;
    font-family: 'Mulish', sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s;
  }

  .vav-save-btn:hover { background: var(--teal-light); }
  .vav-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .vav-saved-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 20px;
    background: var(--teal-bg);
    border: 1px solid var(--teal-border);
    color: var(--teal);
    font-size: 13px;
    font-weight: 600;
    animation: vav-fadein 0.3s ease;
  }

  @keyframes vav-fadein { from { opacity: 0; } to { opacity: 1; } }

  /* ── Tab nav ─────────────────────────────────────────────────────────── */
  .vav-tabs {
    display: flex;
    gap: 0;
    padding: 0 28px;
    background: var(--white);
    border-bottom: 1px solid var(--border);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .vav-tab {
    padding: 11px 16px;
    border: none; background: none;
    font-family: 'Mulish', sans-serif;
    font-size: 13px; font-weight: 600;
    color: var(--ink-light);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .vav-tab.active { color: var(--teal); border-bottom-color: var(--teal); }

  /* ── Content ─────────────────────────────────────────────────────────── */
  .vav-content { padding: 24px 28px 60px; max-width: 680px; }

  .vav-section { margin-bottom: 28px; }

  .vav-section-title {
    font-family: 'DM Serif Display', serif;
    font-size: 18px;
    color: var(--slate-blue);
    margin: 0 0 5px;
  }

  .vav-section-desc {
    font-size: 13px;
    color: var(--ink-light);
    margin: 0 0 16px;
    line-height: 1.55;
  }

  /* ── Days grid ───────────────────────────────────────────────────────── */
  .vav-day-row {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  .vav-day-label {
    font-size: 14px;
    font-weight: 700;
    color: var(--ink);
    width: 90px;
    flex-shrink: 0;
  }

  .vav-day-toggle {
    width: 40px; height: 22px;
    border-radius: 11px;
    border: none;
    cursor: pointer;
    padding: 2px;
    transition: background 0.2s;
    flex-shrink: 0;
  }

  .vav-day-toggle.on  { background: var(--teal); }
  .vav-day-toggle.off { background: var(--border); }

  .vav-day-toggle-knob {
    width: 18px; height: 18px;
    border-radius: 50%;
    background: white;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  .vav-day-toggle.on  .vav-day-toggle-knob { transform: translateX(18px); }
  .vav-day-toggle.off .vav-day-toggle-knob { transform: translateX(0); }

  .vav-time-blocks { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 200px; }

  .vav-time-block {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .vav-time-input {
    background: var(--off-white);
    border: 1.5px solid var(--border);
    border-radius: 7px;
    padding: 6px 10px;
    font-family: 'Mulish', sans-serif;
    font-size: 13px;
    color: var(--ink);
    outline: none;
    width: 100px;
    transition: border-color 0.15s;
  }

  .vav-time-input:focus { border-color: var(--teal); background: var(--white); }

  .vav-time-sep { font-size: 12px; color: var(--ink-light); }

  .vav-add-block {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    border: 1px dashed var(--teal-border);
    border-radius: 7px;
    background: var(--teal-bg);
    color: var(--teal);
    font-family: 'Mulish', sans-serif;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .vav-add-block:hover { opacity: 0.8; }

  @media (max-width: 768px) {
    .vav-day-row {
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px 14px;
    }
    .vav-day-label { width: auto; }
    .vav-time-blocks { width: 100%; flex-basis: 100%; }
    .vav-time-input { flex: 1; min-width: 0; width: auto; }
  }

  .vav-remove-block {
    background: none; border: none; cursor: pointer;
    color: var(--ink-light); padding: 2px;
    transition: color 0.15s;
  }

  .vav-remove-block:hover { color: var(--red); }

  .vav-day-disabled { opacity: 0.4; pointer-events: none; }

  /* ── Session types ───────────────────────────────────────────────────── */
  .vav-session-cards { display: flex; flex-direction: column; gap: 10px; }

  .vav-session-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px;
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 12px;
    align-items: center;
  }

  .vav-session-field { display: flex; flex-direction: column; gap: 4px; }

  .vav-session-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-light);
  }

  .vav-session-input {
    background: var(--off-white);
    border: 1.5px solid var(--border);
    border-radius: 7px;
    padding: 7px 10px;
    font-family: 'Mulish', sans-serif;
    font-size: 13px;
    color: var(--ink);
    outline: none;
    transition: border-color 0.15s;
  }

  .vav-session-input:focus { border-color: var(--teal); background: var(--white); }

  .vav-add-session-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 18px;
    border-radius: 10px;
    border: 1.5px dashed var(--teal-border);
    background: var(--teal-bg);
    color: var(--teal);
    font-family: 'Mulish', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s;
    width: 100%;
    justify-content: center;
  }

  .vav-add-session-btn:hover { opacity: 0.8; }

  /* ── Timezone select ─────────────────────────────────────────────────── */
  .vav-select {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 9px;
    padding: 10px 36px 10px 14px;
    font-family: 'Mulish', sans-serif;
    font-size: 14px;
    color: var(--ink);
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238a96b0' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    cursor: pointer;
    width: 100%;
    max-width: 360px;
    transition: border-color 0.15s;
  }

  .vav-select:focus { border-color: var(--teal); }

  /* ── Blocked dates ───────────────────────────────────────────────────── */
  .vav-blocked-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }

  .vav-blocked-row {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 13px;
  }

  .vav-blocked-dates { flex: 1; font-weight: 600; color: var(--ink); }
  .vav-blocked-reason { font-size: 12px; color: var(--ink-light); }

  .vav-blocked-delete {
    background: none; border: none; cursor: pointer;
    color: var(--ink-light); padding: 3px;
    transition: color 0.15s;
  }

  .vav-blocked-delete:hover { color: var(--red); }

  .vav-add-blocked {
    display: grid;
    grid-template-columns: 1fr 1fr 2fr auto;
    gap: 10px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 12px 14px;
    align-items: end;
  }

  .vav-input-group { display: flex; flex-direction: column; gap: 4px; }
  .vav-input-label {
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--ink-light);
  }

  .vav-date-input {
    background: var(--off-white);
    border: 1.5px solid var(--border);
    border-radius: 7px;
    padding: 8px 10px;
    font-family: 'Mulish', sans-serif;
    font-size: 13px;
    color: var(--ink);
    outline: none;
    transition: border-color 0.15s;
  }

  .vav-date-input:focus { border-color: var(--teal); background: var(--white); }

  .vav-add-blocked-btn {
    padding: 9px 16px;
    border-radius: 8px;
    background: var(--slate-blue);
    border: none;
    color: white;
    font-family: 'Mulish', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
  }

  .vav-add-blocked-btn:hover { background: var(--slate-mid); }

  /* ── Mode toggle section ─────────────────────────────────────────────── */
  .vav-mode-cards { display: flex; flex-direction: column; gap: 10px; }

  .vav-mode-card {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 14px;
    padding: 18px;
    transition: border-color 0.2s;
  }

  .vav-mode-card.active { border-color: var(--teal); }

  .vav-mode-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .vav-mode-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--ink);
    margin: 0 0 3px;
  }

  .vav-mode-desc {
    font-size: 13px;
    color: var(--ink-light);
    line-height: 1.55;
    margin: 0;
  }

  .vav-mode-extra {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .vav-url-input {
    width: 100%;
    background: var(--off-white);
    border: 1.5px solid var(--border);
    border-radius: 8px;
    padding: 9px 12px;
    font-family: 'Mulish', sans-serif;
    font-size: 13px;
    color: var(--ink);
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s;
  }

  .vav-url-input:focus { border-color: var(--teal); background: var(--white); }

  .vav-public-link {
    font-size: 12px;
    color: var(--teal);
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    flex-wrap: wrap;
  }
  .vav-public-link a { color: var(--teal); text-decoration: none; }
  .vav-public-link a:hover { text-decoration: underline; }
  .vav-copy-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: var(--ink-mid, #44514e);
    background: var(--off-white, #f6f5f1);
    border: 1px solid var(--border, #e2ded4);
    border-radius: 999px;
    padding: 4px 10px;
    cursor: pointer;
    transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease;
  }
  .vav-copy-btn:hover { background: #fff; border-color: var(--teal); color: var(--teal); }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCENT_SWATCHES = [
  { name: "Forest",       hex: "#1a5a44" },
  { name: "Sage",         hex: "#4a8a68" },
  { name: "Deep teal",    hex: "#1f6b70" },
  { name: "Indigo",       hex: "#3b4a8a" },
  { name: "Plum",         hex: "#6b3a63" },
  { name: "Clay",         hex: "#9c4a2f" },
  { name: "Antique gold", hex: "#8a6a12" },
  { name: "Charcoal",     hex: "#33352f" },
];


const DAYS = [
  { id: 0, label: "Monday" },
  { id: 1, label: "Tuesday" },
  { id: 2, label: "Wednesday" },
  { id: 3, label: "Thursday" },
  { id: 4, label: "Friday" },
  { id: 5, label: "Saturday" },
  { id: 6, label: "Sunday" },
];

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
];

const DURATIONS = [30, 45, 60, 75, 90, 120];

// ─── Toggle component ─────────────────────────────────────────────────────────

function DayToggle({ on, onToggle }) {
  return (
    <button className={`vav-day-toggle ${on ? "on" : "off"}`} onClick={onToggle}>
      <div className="vav-day-toggle-knob" />
    </button>
  );
}

// ─── External Payment Settings (Tip & Donation Links) ────────────────────────

function ExternalPaymentSettings() {
  const [venmo, setVenmo] = useState("");
  const [cashapp, setCashapp] = useState("");
  const [paypal, setPaypal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  // Tip and donation handles belong to the practitioner, so no storefront id is sent.

  useEffect(() => {
    apiFetch(`/session-feedback/external-payment`)
      .then(data => {
        const ep = data?.external_payment || {};
        setVenmo(ep.venmo_handle || "");
        setCashapp(ep.cashapp_handle || "");
        setPaypal(ep.paypal_handle || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/session-feedback/external-payment`, {
        method: "PUT",
        body: JSON.stringify({
          venmo_handle: venmo || null,
          cashapp_handle: cashapp || null,
          paypal_handle: paypal || null,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.message === "No vendor found"
        ? "Your practice profile isn't set up yet — save your booking profile first."
        : (e?.message || "We couldn't save those handles. Please try again."));
    }
    setSaving(false);
  };

  if (loading) return <p style={{ color: "var(--ink-light)", padding: 24 }}>Loading...</p>;

  return (
    <div style={{ maxWidth: 500 }}>
      <h3 style={{ fontFamily: "'Spectral', serif", fontSize: 18, color: "var(--ink)", margin: "0 0 6px" }}>
        External Payment Links
      </h3>
      <p style={{ fontSize: 13, color: "var(--ink-light)", margin: "0 0 20px", lineHeight: 1.5 }}>
        Add your payment handles so clients can tip or donate directly without processing fees.
        If none are set, clients will use credit/debit card through the platform.
      </p>

      {[
        { label: "Venmo", value: venmo, set: setVenmo, placeholder: "your-venmo-username", prefix: "@" },
        { label: "Cash App", value: cashapp, set: setCashapp, placeholder: "$yourcashtag", prefix: "" },
        { label: "PayPal", value: paypal, set: setPaypal, placeholder: "your-paypal-me", prefix: "" },
      ].map(f => (
        <div key={f.label} style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>
            {f.label}
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {f.prefix && <span style={{ fontSize: 14, color: "var(--ink-light)" }}>{f.prefix}</span>}
            <input
              className="vav-url-input"
              style={{ flex: 1 }}
              placeholder={f.placeholder}
              value={f.value}
              onChange={e => f.set(e.target.value)}
              data-testid={`ext-pay-${f.label.toLowerCase().replace(/\s/g, '')}`}
            />
          </div>
        </div>
      ))}

      <button
        className="vav-save-btn"
        onClick={save}
        disabled={saving}
        data-testid="save-external-payment"
        style={{ marginTop: 8 }}
      >
        {saving ? "Saving..." : saved ? "Saved!" : "Save Payment Links"}
      </button>
      {error && (
        <p data-testid="ext-pay-error" style={{ marginTop: 10, fontSize: 13, color: "#b4402f" }}>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const CancellationPolicyEditor = ({ policy, setPolicy }) => {
  const set = (key, value) => {
    const n = value === "" ? "" : Math.max(0, parseInt(value, 10) || 0);
    setPolicy(p => ({ ...p, [key]: n }));
  };
  const full = Number(policy.full_refund_hours) || 0;
  const partial = Number(policy.partial_refund_hours) || 0;
  const pct = Number(policy.partial_refund_percent) || 0;
  const outOfOrder = partial > full;

  return (
    <div className="vav-fadein" data-testid="cancellation-policy-editor">
      <div className="vav-section">
        <p className="vav-section-title">Your cancellation policy</p>
        <p className="vav-section-desc">
          These are your rules — clients see them before they book and again before they
          cancel, and the system enforces them automatically.
        </p>

        <div style={{ display: "grid", gap: 18, maxWidth: 560, marginTop: 18 }}>
          <div className="vav-session-field">
            <label className="vav-session-label">Full refund if cancelled more than</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="number" min="0" className="vav-session-input" style={{ width: 100 }}
                value={policy.full_refund_hours}
                onChange={e => set("full_refund_hours", e.target.value)}
                data-testid="policy-full-refund-hours"
              />
              <span style={{ fontSize: 13, color: "var(--ink-mid)" }}>hours before the session</span>
            </div>
            <span style={{ fontSize: 12, color: "var(--ink-light)" }}>
              Suggestion: 24–48 hours is what most clients expect.
            </span>
          </div>

          <div className="vav-session-field">
            <label className="vav-session-label">Partial refund down to</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <input
                type="number" min="0" className="vav-session-input" style={{ width: 100 }}
                value={policy.partial_refund_hours}
                onChange={e => set("partial_refund_hours", e.target.value)}
                data-testid="policy-partial-refund-hours"
              />
              <span style={{ fontSize: 13, color: "var(--ink-mid)" }}>hours before, refunding</span>
              <input
                type="number" min="0" max="100" className="vav-session-input" style={{ width: 90 }}
                value={policy.partial_refund_percent}
                onChange={e => set("partial_refund_percent", Math.min(100, parseInt(e.target.value, 10) || 0))}
                data-testid="policy-partial-refund-percent"
              />
              <span style={{ fontSize: 13, color: "var(--ink-mid)" }}>%</span>
            </div>
            <span style={{ fontSize: 12, color: "var(--ink-light)" }}>
              Suggestion: 12–24 hours still gives you a chance to refill the slot, and 50% is
              the common middle ground.
            </span>
          </div>
        </div>

        {outOfOrder && (
          <div
            style={{
              marginTop: 18, padding: "10px 14px", borderRadius: 8,
              background: "var(--amber-bg)", color: "var(--amber)", fontSize: 13,
            }}
            data-testid="policy-order-warning"
          >
            Your partial-refund window is longer than your full-refund window, so the partial
            band never applies. Set the partial hours lower than {full} to use it.
          </div>
        )}

        <div
          style={{
            marginTop: 22, padding: "14px 16px", borderRadius: 10,
            background: "var(--teal-bg)", border: "1px solid var(--teal-border)",
          }}
          data-testid="policy-plain-english"
        >
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
                      textTransform: "uppercase", color: "var(--teal)", marginBottom: 8 }}>
            What your clients will read
          </p>
          <ul style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.7, paddingLeft: 18 }}>
            <li>Cancel more than <strong>{full} hours</strong> ahead — full refund of the session price.</li>
            {!outOfOrder && pct > 0 && partial < full && (
              <li>
                Between <strong>{full}</strong> and <strong>{partial} hours</strong> ahead —{" "}
                <strong>{pct}%</strong> of the session price refunded.
              </li>
            )}
            <li>Inside <strong>{outOfOrder ? full : partial} hours</strong> — no refund of the session price.</li>
            <li>The card processing fee is never refundable — Stripe keeps it either way.</li>
          </ul>
        </div>

        <div
          style={{
            marginTop: 16, padding: "14px 16px", borderRadius: 10,
            background: "var(--off-white)", border: "1px solid var(--border)",
          }}
        >
          <p style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.6 }}>
            <strong style={{ color: "var(--ink)" }}>If you cancel</strong> a session you've already
            confirmed, your client is refunded everything they paid — processing fee included — and
            that fee comes off your next payout, because Stripe doesn't return it. Rescheduling
            costs you nothing, so offer another time where you can.
          </p>
        </div>
      </div>
    </div>
  );
};

export default function VendorAvailability() {
  const [tab, setTab]           = useState("schedule");
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [savedSession, setSavedSession] = useState(null);
  const [sessionErrors, setSessionErrors] = useState({});
  const [error, setError]       = useState("");
  const [subStatus, setSubStatus] = useState(null);
  const [loadedProfile, setLoadedProfile] = useState(null);
  const [timezone, setTimezone] = useState("America/New_York");
  const [loading, setLoading]   = useState(true);

  const [schedule, setSchedule] = useState({
    0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [],
  });

  const [sessions, setSessions] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [dateAvailability, setDateAvailability] = useState([]); // [{date, start_time, end_time, recurring}]
  const [newDateSlot, setNewDateSlot] = useState({ date: "", start_time: "09:00", end_time: "17:00", recurring: false });
  const [newBlock, setNewBlock] = useState({ date_start: "", date_end: "", reason: "" });
  const [modes, setModes] = useState({ in_app: true, public: false, external: false });
  // What the SERVER last confirmed, as opposed to what the toggles show. A refused save used
  // to leave the page advertising a public URL that did not exist.
  const [publishedPublic, setPublishedPublic] = useState(false);
  const [copied, setCopied] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [externalAgent, setExternalAgent] = useState("");
  const [bookingSlug, setBookingSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [practitionerPhoto, setPractitionerPhoto] = useState(null);
  const [bio, setBio] = useState("");
  const [bannerUrl, setBannerUrl] = useState(null);
  const [accent, setAccent] = useState("#1a4a3a");
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bioSuggestion, setBioSuggestion] = useState(null);
  const [policy, setPolicy] = useState({
    full_refund_hours: 48, partial_refund_hours: 24, partial_refund_percent: 50,
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [practiceForms, setPracticeForms] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const profile = await apiFetch("/practitioner/booking-profile").catch(() => null);
        if (profile) {
          setTimezone(profile.timezone || "America/New_York");
          setSessions((profile.session_types || []).map(st => {
            // Migrate old intake_form_url to intake_forms array
            if (st.intake_form_url && (!st.intake_forms || st.intake_forms.length === 0)) {
              return { ...st, intake_forms: [st.intake_form_url] };
            }
            return { ...st, intake_forms: st.intake_forms || [] };
          }));
          setPracticeForms(profile.practice_intake_forms || []);
          setExternalUrl(profile.external_url || "");
          setExternalAgent(profile.external_agent || "");
          setBookingSlug(profile.booking_slug || "");
          setDisplayName(profile.display_name || "");
          setPractitionerPhoto(profile.avatar_url || null);
          setBio(profile.bio || "");
          setBannerUrl(profile.banner_url || null);
          setAccent(profile.widget_accent || "#1a4a3a");
          const mEnabled = profile.modes_enabled || ["in_app"];
          setModes({ in_app: mEnabled.includes("in_app"), public: mEnabled.includes("public"), external: mEnabled.includes("external") });
          setPublishedPublic(Boolean(profile.public_enabled));
          const sched = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
          for (const slot of (profile.availability || [])) {
            sched[slot.day_of_week] = sched[slot.day_of_week] || [];
            // Older profiles stored `start`/`end`. Reading those as undefined sent a payload
            // with no times at all, which came back as ten "Field required" errors.
            sched[slot.day_of_week].push({
              start_time: slot.start_time || slot.start || "09:00",
              end_time:   slot.end_time   || slot.end   || "17:00",
            });
          }
          setSchedule(sched);
          setDateAvailability(profile.date_availability || []);
          if (profile.cancellation_policy) setPolicy(profile.cancellation_policy);
          setLoadedProfile(profile);
        }
        const blocked = await apiFetch("/availability/me/blocked").catch(() => []);
        setBlockedDates(blocked);
        apiFetch("/practitioner/bio-suggestion")
          .then(d => setBioSuggestion(d?.bio ? d : null))
          .catch(() => {});
        const status = await apiFetch("/practitioner/subscription/status", { skipVendorId: true })
          .catch(() => null);
        setSubStatus(status);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const toggleDay = (dow) => {
    setSchedule(prev => {
      const existing = prev[dow];
      return {
        ...prev,
        [dow]: existing.length > 0 ? [] : [{ start_time: "09:00", end_time: "17:00" }],
      };
    });
  };

  const updateBlock = (dow, idx, field, value) => {
    setSchedule(prev => {
      const blocks = [...prev[dow]];
      blocks[idx] = { ...blocks[idx], [field]: value };
      return { ...prev, [dow]: blocks };
    });
  };

  const addBlock = (dow) => {
    setSchedule(prev => ({
      ...prev,
      [dow]: [...prev[dow], { start_time: "09:00", end_time: "17:00" }],
    }));
  };

  const removeBlock = (dow, idx) => {
    setSchedule(prev => {
      const blocks = prev[dow].filter((_, i) => i !== idx);
      return { ...prev, [dow]: blocks };
    });
  };

  const addDateSlot = () => {
    if (!newDateSlot.date) return;
    setDateAvailability(prev => [...prev, { ...newDateSlot }]);
    setNewDateSlot({ date: "", start_time: "09:00", end_time: "17:00", recurring: false });
  };

  const addDateHours = (dateStr, recurring) => {
    setDateAvailability(prev => [...prev, { date: dateStr, start_time: "09:00", end_time: "17:00", recurring }]);
  };

  const removeDateSlot = (idx) => {
    setDateAvailability(prev => prev.filter((_, i) => i !== idx));
  };

  const updateDateSlot = (idx, field, value) => {
    setDateAvailability(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addSession = () => {
    setSessions(prev => [...prev, {
      id:                   `s${Date.now()}`,
      name:                 "",
      healing_duration:     60,
      before_buffer:        0,
      after_buffer:         0,
      total_duration:       60,
      price:                0,
      only_session_for_day: false,
      hard_start_time:      null,
      location_mode:        "video",
    }]);
  };

  const updateSession = (id, field, value) => {
    setSessions(prev => prev.map(s => {
      if (s.id !== id) return s;
      const updated = { ...s, [field]: value };
      // Recalculate total_duration when buffers or healing change
      if (['healing_duration', 'before_buffer', 'after_buffer'].includes(field)) {
        updated.total_duration = (updated.healing_duration || 0) + (updated.before_buffer || 0) + (updated.after_buffer || 0);
      }
      return updated;
    }));
  };

  const removeSession = (id) => {
    setSessions(prev => prev.filter(s => s.id !== id));
  };

  // Drag to reorder. The array order is what the booking page renders, so moving a card here
  // is the whole feature — nothing else needs to change.
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const dropSession = (toIndex) => {
    setSessions(prev => {
      if (dragFrom === null || dragFrom === toIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragFrom, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDragFrom(null);
    setDragOver(null);
  };

  const addBlockedDate = async () => {
    if (!newBlock.date_start || !newBlock.date_end) return;
    try {
      const result = await apiFetch("/availability/me/blocked", {
        method: "POST", body: JSON.stringify(newBlock),
      });
      setBlockedDates(prev => [...prev, result]);
    } catch (e) { console.error(e); }
    setNewBlock({ date_start: "", date_end: "", reason: "" });
  };

  const removeBlockedDate = async (id) => {
    try {
      await apiFetch(`/availability/me/blocked/${id}`, { method: "DELETE" });
      setBlockedDates(prev => prev.filter(b => b.id !== id));
    } catch (e) { console.error(e); }
  };

  const save = async ({ sessionId } = {}) => {
    // A package with no price used to save happily, show the client the undiscounted total and
    // then charge them nothing — four free sessions the practitioner still had to deliver.
    // His call (Jun 2026): block the save.
    const priceless = sessions.filter(
      s => s.offer_as_package && !(Number(s.package_price) > 0));
    if (priceless.length) {
      setSessionErrors(Object.fromEntries(priceless.map(s => [s.id,
        "Give the package a price — without one the package would be given away free."])));
      setError(
        `"${priceless[0].name || priceless[0].title || 'One of your offerings'}" is offered as a `
        + "package with no package price. Add the price, or switch the package off.");
      return;
    }
    setSessionErrors({});
    setSaving(true);
    setError("");
    try {
      // Build availability array from schedule
      const availability = [];
      for (const [dow, blocks] of Object.entries(schedule)) {
        for (const block of blocks) {
          availability.push({
            day_of_week: parseInt(dow),
            start_time:  block.start_time,
            end_time:    block.end_time,
          });
        }
      }

      const modesEnabled = Object.entries(modes)
        .filter(([, v]) => v)
        .map(([k]) => k);

      // Real: PUT /api/practitioner/booking-profile
      await apiFetch("/practitioner/booking-profile", {
        method: "PUT",
        body: JSON.stringify({
          timezone, availability, date_availability: dateAvailability,
          session_types: sessions,
          practice_intake_forms: practiceForms,
          modes_enabled: modesEnabled,
          display_name: displayName || null,
          bio: bio || null,
          widget_accent: accent,
          // "" rather than null — the profile service ignores null, so a null here would
          // silently keep a banner the practitioner has just removed.
          banner_url: bannerUrl || "",
          public_enabled: modes.public,
          external_url: externalUrl || null,
          external_agent: externalAgent || null,
          cancellation_policy: policy,
        }),
      });

      setSaved(true);
      setPublishedPublic(Boolean(modes.public));   // confirmed by the server, not by the toggle
      setTimeout(() => setSaved(false), 6000);
      if (sessionId) {
        setSavedSession(sessionId);
        // Six seconds, not three: the whole trouble here was not being able to tell whether
        // an offering had saved, so the confirmation should outlast a glance away.
        setTimeout(() => setSavedSession(null), 6000);
      }
    } catch (e) {
      // Without this the rejected request escaped as an uncaught runtime error and took
      // the whole page down with a red overlay instead of telling him what went wrong.
      setError(e.message || "Your changes could not be saved.");
      // The banner lives at the top of a long page. Saving from a session card means the
      // button is far below it, so a failed save looked like nothing happening at all —
      // which is how an intake form appeared to be attached when it never saved.
      if (sessionId) {
        setSessionErrors({ [sessionId]: e.message || "This offering could not be saved." });
      }
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = `${WEB_ORIGIN}/booking/${bookingSlug}`;
  const embedCode = `<iframe src="${publicUrl}" width="100%" height="700" style="border:none;" />`;

  const copyToClipboard = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // clipboard API needs a secure context and permission; this always works
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/practitioner/photo", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.file_url) setPractitionerPhoto(data.file_url);
    } catch (err) {
      console.error("Photo upload failed:", err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/practitioner/banner", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.file_url) setBannerUrl(data.file_url);
    } catch {
      setError("Your banner image could not be uploaded.");
    } finally {
      setUploadingBanner(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="vav-root">
        <div className="vav-header">
          <div>
            <h1>Availability & Booking</h1>
            <p>Configure when and how clients can book sessions with you.</p>
          </div>
          <div>
            {saved ? (
              <div className="vav-saved-pill"><Check size={14} /> Saved</div>
            ) : (
              <button className="vav-save-btn" onClick={() => save()} disabled={saving} data-testid="availability-save-btn">
                {saving ? <><Loader size={14} style={{ animation: "spin 1s linear infinite" }} /> Saving...</> : <><Save size={14} /> Save changes</>}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div
            data-testid="availability-save-error"
            style={{
              display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 18,
              border: "1px solid var(--red-border, #e6c3bd)", background: "var(--red-bg, #fdf0ee)",
              borderRadius: 10, padding: "12px 14px", fontSize: 13, lineHeight: 1.6,
              color: "var(--red, #b4402f)",
            }}
          >
            <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        <BookingAccessNotice status={subStatus} wantsInApp={modes.in_app || modes.public} />

        <div className="vav-tabs">
          {[
            { id: "schedule", label: "Weekly Schedule" },
            { id: "sessions", label: "Session Types" },
            { id: "blocked",  label: "Blocked Dates" },
            { id: "limits",   label: "Limits & Calendar" },
            { id: "modes",    label: "Booking Modes" },
            { id: "payment",  label: "Tip & Donation Links" },
            { id: "cancellation", label: "Cancellation Policy" },
            { id: "emails",   label: "Client Emails" },
          ].map(t => (
            <button key={t.id} className={`vav-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="vav-content">

          {/* ── Schedule tab ── */}
          {tab === "schedule" && (
            <>
              <div className="vav-section">
                <p className="vav-section-title">Practitioner profile</p>
                <p className="vav-section-desc">
                  Your name and photo as clients see them when booking sessions — separate from your storefront business name and logo.
                </p>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  {/* Photo upload */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
                      background: practitionerPhoto ? 'transparent' : 'linear-gradient(135deg, var(--forest, #1a5a44), var(--sage, #8aaf7a))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '3px solid var(--border)', flexShrink: 0,
                    }} data-testid="practitioner-photo-preview">
                      {practitionerPhoto ? (
                        <img src={practitionerPhoto} alt="Practitioner"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: 32, color: 'white', fontFamily: "'Crimson Pro', serif" }}>
                          {(displayName || 'P')[0]}
                        </span>
                      )}
                    </div>
                    <label style={{
                      fontSize: 12, color: 'var(--forest, #1a5a44)', cursor: 'pointer',
                      fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                    }} data-testid="practitioner-photo-upload-btn">
                      {uploadingPhoto ? 'Uploading...' : practitionerPhoto ? 'Change photo' : 'Add photo'}
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploadingPhoto} />
                    </label>
                  </div>
                  {/* Name input */}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 4 }}>
                      Display name
                    </label>
                    <input
                      className="vav-session-input"
                      placeholder="e.g. Sarah Mitchell, Dr. James Rivera"
                      value={displayName}
                      onChange={e => setDisplayName(e.target.value)}
                      data-testid="practitioner-display-name"
                      style={{ maxWidth: 360, fontSize: 14 }}
                    />
                  </div>
                </div>

                {/* Bio — the booking page used to take whatever onboarding wrote and there was
                    no way to change it, so a stranger read a paragraph meant for somewhere else. */}
                <div style={{ marginTop: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 4 }}>
                    Bio on your booking page
                  </label>
                  <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 6px' }}>
                    A few lines for someone who has never met you, shown under your name.
                  </p>
                  <textarea
                    className="vav-session-input"
                    style={{ minHeight: 90, resize: 'vertical', lineHeight: 1.5, width: '100%',
                             fontFamily: "'Source Sans 3', sans-serif" }}
                    placeholder="I work with people who…"
                    value={bio}
                    maxLength={2000}
                    onChange={e => setBio(e.target.value)}
                    data-testid="practitioner-bio-input"
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-light)' }}>{bio.length}/2000</span>
                    {bioSuggestion?.bio && bioSuggestion.bio !== bio && (
                      <button
                        type="button"
                        onClick={() => setBio(bioSuggestion.bio.slice(0, 2000))}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                 fontSize: 12, fontWeight: 600, color: 'var(--forest, #1a5a44)',
                                 textDecoration: 'underline', fontFamily: "'Source Sans 3', sans-serif" }}
                        data-testid="practitioner-use-existing-bio"
                      >
                        Use the bio from {bioSuggestion.source}
                      </button>
                    )}
                  </div>
                </div>

                {/* Banner — wide across the top, photo overlapping it */}
                <div style={{ marginTop: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 4 }}>
                    Banner image (optional)
                  </label>
                  <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 8px' }}>
                    Sits across the top of your booking page with your photo over it. Wide and
                    short works best — around 1600 × 400.
                  </p>
                  <div style={{
                    position: 'relative', height: 108, borderRadius: 12, overflow: 'hidden',
                    border: '1px solid var(--border)', maxWidth: 560,
                    background: bannerUrl ? `center/cover no-repeat ${cssUrl(bannerUrl)}`
                                          : `linear-gradient(120deg, ${accent}, #1b4a34)`,
                  }} data-testid="practitioner-banner-preview">
                    <div style={{
                      position: 'absolute', left: 20, bottom: -1, width: 54, height: 54,
                      borderRadius: '50%', border: '3px solid white', overflow: 'hidden',
                      background: accent, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: 'white', fontSize: 20,
                      fontFamily: "'Crimson Pro', serif",
                    }}>
                      {practitionerPhoto
                        ? <img src={practitionerPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (displayName || 'P')[0]}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 8 }}>
                    <label style={{ fontSize: 12, color: 'var(--forest, #1a5a44)', cursor: 'pointer', fontWeight: 600 }}
                           data-testid="practitioner-banner-upload-btn">
                      {uploadingBanner ? 'Uploading…' : bannerUrl ? 'Change banner' : 'Add a banner'}
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                             onChange={handleBannerUpload} style={{ display: 'none' }} disabled={uploadingBanner} />
                    </label>
                    {bannerUrl && (
                      <button
                        type="button"
                        onClick={() => setBannerUrl(null)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                 fontSize: 12, color: 'var(--ink-light)', textDecoration: 'underline',
                                 fontFamily: "'Source Sans 3', sans-serif" }}
                        data-testid="practitioner-banner-remove"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '6px 0 0' }}>
                    A banner saves as soon as it uploads. Removing it takes effect when you save.
                  </p>
                </div>

                {/* Accent colour — `widget_accent` has been in the database for months with
                    nothing anywhere to set it, so every booking page was the default green. */}
                <div style={{ marginTop: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 4 }}>
                    Accent colour
                  </label>
                  <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 8px' }}>
                    Used for your buttons and the selected time on your booking page.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {ACCENT_SWATCHES.map(sw => (
                      <button
                        key={sw.hex}
                        type="button"
                        title={sw.name}
                        onClick={() => setAccent(sw.hex)}
                        data-testid={`accent-swatch-${sw.hex.replace('#', '')}`}
                        style={{
                          width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
                          background: sw.hex,
                          border: accent.toLowerCase() === sw.hex.toLowerCase()
                            ? '3px solid var(--ink)' : '1px solid var(--border)',
                        }}
                      />
                    ))}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                                    color: 'var(--ink-mid)', cursor: 'pointer' }}>
                      <input
                        type="color"
                        value={accent}
                        onChange={e => setAccent(e.target.value)}
                        data-testid="accent-custom-picker"
                        style={{ width: 34, height: 30, border: '1px solid var(--border)',
                                 borderRadius: 6, background: 'none', cursor: 'pointer', padding: 2 }}
                      />
                      Custom
                    </label>
                    <span style={{ fontSize: 12, color: 'var(--ink-light)' }} data-testid="accent-current-hex">
                      {accent.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="vav-section">
                <p className="vav-section-title">Timezone</p>
                <p className="vav-section-desc">All availability times are in this timezone.</p>
                <select className="vav-select" value={timezone} onChange={e => setTimezone(e.target.value)}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace("_", " ")}</option>)}
                </select>
              </div>

              <div className="vav-section">
                <p className="vav-section-title">Weekly availability</p>
                <p className="vav-section-desc">Set which days and hours you're available to accept bookings.</p>

                {DAYS.map(({ id: dow, label }) => {
                  const blocks  = schedule[dow] || [];
                  const enabled = blocks.length > 0;
                  return (
                    <div key={dow} className="vav-day-row">
                      <span className="vav-day-label">{label}</span>
                      <DayToggle on={enabled} onToggle={() => toggleDay(dow)} />
                      <div className={`vav-time-blocks ${!enabled ? "vav-day-disabled" : ""}`}>
                        {blocks.map((block, idx) => (
                          <div key={idx} className="vav-time-block">
                            <input
                              className="vav-time-input"
                              type="time"
                              value={block.start_time}
                              onChange={e => updateBlock(dow, idx, "start_time", e.target.value)}
                            />
                            <span className="vav-time-sep">to</span>
                            <input
                              className="vav-time-input"
                              type="time"
                              value={block.end_time}
                              onChange={e => updateBlock(dow, idx, "end_time", e.target.value)}
                            />
                            {blocks.length > 1 && (
                              <button className="vav-remove-block" onClick={() => removeBlock(dow, idx)}>
                                <X size={13} />
                              </button>
                            )}
                          </div>
                        ))}
                        {enabled && (
                          <button className="vav-add-block" onClick={() => addBlock(dow)}>
                            <Plus size={11} /> Add hours
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Date-specific availability ── */}
              <div className="vav-section">
                <p className="vav-section-title">Date-specific availability</p>
                <p className="vav-section-desc">
                  Set hours for specific dates. Toggle "Recurring" to repeat the same hours every week on that day.
                  These override your weekly schedule for matching dates.
                </p>

                {(() => {
                  // Group date entries by date string
                  const grouped = {};
                  dateAvailability.forEach((slot, idx) => {
                    if (!grouped[slot.date]) grouped[slot.date] = [];
                    grouped[slot.date].push({ ...slot, _idx: idx });
                  });

                  return Object.entries(grouped).map(([dateStr, slots]) => {
                    const dayName = dateStr ? new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
                    const isRecurring = slots[0]?.recurring;
                    return (
                      <div key={dateStr} style={{
                        padding: '12px 14px', marginBottom: 8,
                        background: isRecurring ? 'var(--teal-bg)' : 'var(--off-white)',
                        border: `1px solid ${isRecurring ? 'var(--teal-border)' : 'var(--border)'}`,
                        borderRadius: 10,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: slots.length > 1 ? 8 : 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 110, color: 'var(--ink)' }}>{dayName}</span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer', color: isRecurring ? 'var(--teal)' : 'var(--ink-light)', marginLeft: 'auto' }}>
                            <input type="checkbox" checked={isRecurring}
                              onChange={e => {
                                const val = e.target.checked;
                                slots.forEach(s => updateDateSlot(s._idx, 'recurring', val));
                              }}
                              style={{ accentColor: 'var(--teal)' }} />
                            Recurring
                          </label>
                        </div>

                        {slots.map((slot) => (
                          <div key={slot._idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <input className="vav-time-input" type="time" value={slot.start_time}
                              onChange={e => updateDateSlot(slot._idx, 'start_time', e.target.value)} />
                            <span className="vav-time-sep">to</span>
                            <input className="vav-time-input" type="time" value={slot.end_time}
                              onChange={e => updateDateSlot(slot._idx, 'end_time', e.target.value)} />
                            <button className="vav-remove-block" onClick={() => removeDateSlot(slot._idx)}>
                              <X size={13} />
                            </button>
                          </div>
                        ))}

                        <button className="vav-add-block" onClick={() => addDateHours(dateStr, isRecurring)}
                          style={{ marginTop: 4 }} data-testid={`add-hours-${dateStr}`}>
                          <Plus size={11} /> Add hours
                        </button>
                      </div>
                    );
                  });
                })()}

                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '10px 14px',
                  background: 'var(--off-white)',
                  border: '1px dashed var(--border)',
                  borderRadius: 8,
                  marginTop: 8,
                }}>
                  <input type="date" value={newDateSlot.date}
                    onChange={e => setNewDateSlot(p => ({ ...p, date: e.target.value }))}
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, fontFamily: "'Source Sans 3', sans-serif" }}
                    data-testid="date-avail-picker"
                  />
                  <input className="vav-time-input" type="time" value={newDateSlot.start_time}
                    onChange={e => setNewDateSlot(p => ({ ...p, start_time: e.target.value }))} />
                  <span className="vav-time-sep">to</span>
                  <input className="vav-time-input" type="time" value={newDateSlot.end_time}
                    onChange={e => setNewDateSlot(p => ({ ...p, end_time: e.target.value }))} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer', color: 'var(--ink-light)' }}>
                    <input type="checkbox" checked={newDateSlot.recurring}
                      onChange={e => setNewDateSlot(p => ({ ...p, recurring: e.target.checked }))}
                      style={{ accentColor: 'var(--teal)' }} />
                    Recurring
                  </label>
                  <button className="vav-add-block" onClick={addDateSlot} disabled={!newDateSlot.date}
                    data-testid="add-date-avail-btn">
                    <Plus size={11} /> Add date
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── Session types tab ── */}
          {tab === "sessions" && (
            <div className="vav-section">
              <p className="vav-section-title">Session offerings</p>
              <p className="vav-section-desc">Define your healing modalities, durations, buffers, and pricing. Each session type appears on your booking page for clients to choose from.</p>

              <div className="vav-session-card" style={{ marginBottom: 18 }} data-testid="practice-intake-forms">
                <label className="vav-session-label">Forms for every session (optional)</label>
                <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 8px' }}>
                  Your standing waiver or health history — asked for on every session you run, so
                  you never re-add it to each offering. Each session type can add its own below.
                </p>
                <IntakeFormsEditor
                  forms={practiceForms}
                  onChange={setPracticeForms}
                  testPrefix="practice-intake"
                  apiFetch={apiFetch}
                />
              </div>

              <div className="vav-session-cards">
                {sessions.map((s, si) => (
                  <div key={s.id} className="vav-session-card"
                       style={{ display: 'flex', flexDirection: 'column', gap: 14,
                                opacity: dragFrom === si ? 0.5 : 1,
                                borderTop: dragOver === si && dragFrom !== si
                                  ? '2px solid var(--forest)' : undefined }}
                       onDragOver={(e) => { e.preventDefault(); setDragOver(si); }}
                       onDrop={(e) => { e.preventDefault(); dropSession(si); }}
                       data-testid={`session-card-${s.id}`}>
                    {/* The order here IS the order on her booking page, so she arranges it by
                        hand rather than renaming things to sort (owner, Jun 2026). */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                                  borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: 8 }}>
                      <span
                        draggable
                        onDragStart={() => setDragFrom(si)}
                        onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                        title="Drag to reorder — this is the order clients see"
                        style={{ cursor: 'grab', display: 'flex', alignItems: 'center',
                                 color: 'var(--ink-light)' }}
                        data-testid={`session-drag-${s.id}`}
                      >
                        <GripVertical size={15} />
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--ink-light)' }}>
                        {si + 1} of {sessions.length}
                      </span>
                      <div style={{ flex: 1 }} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
                                      cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(s.asleep)}
                          onChange={e => updateSession(s.id, 'asleep', e.target.checked)}
                          data-testid={`session-asleep-toggle-${s.id}`}
                        />
                        Asleep — still listed, not bookable
                      </label>
                      {s.asleep && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                          until
                          <input
                            className="vav-session-input"
                            type="date"
                            value={s.asleep_until || ''}
                            onChange={e => updateSession(s.id, 'asleep_until', e.target.value || null)}
                            style={{ width: 150 }}
                            data-testid={`session-asleep-until-${s.id}`}
                          />
                          <span style={{ color: 'var(--ink-light)' }}>
                            {s.asleep_until ? '' : 'you wake it'}
                          </span>
                        </label>
                      )}
                    </div>
                    {/* Row 1: Name + Price + Delete */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                      <div className="vav-session-field" style={{ flex: 1 }}>
                        <label className="vav-session-label">Modality / Session Name</label>
                        <input
                          className="vav-session-input"
                          placeholder="e.g. Reiki Healing, Chakra Balancing, Intuitive Reading..."
                          value={s.name || s.title || ""}
                          onChange={e => updateSession(s.id, "name", e.target.value)}
                          data-testid={`session-name-${s.id}`}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="vav-session-field" style={{ width: 110 }}>
                        <label className="vav-session-label">Cost (USD)</label>
                        <input
                          className="vav-session-input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={s.price || ""}
                          onChange={e => updateSession(s.id, "price", parseFloat(e.target.value) || 0)}
                          data-testid={`session-price-${s.id}`}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <button
                        onClick={() => removeSession(s.id)}
                        style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", padding: 4, marginBottom: 2 }}
                        data-testid={`session-delete-${s.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    {/* Row 2: Description — clients see it behind a "Details" link on the card */}
                    <div className="vav-session-field">
                      <label className="vav-session-label">Description (optional)</label>
                      <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 4px' }}>
                        What happens in this session, who it suits, what to expect. Clients open
                        it from a <strong>Details</strong> link on your booking page, so it can be
                        as long as it needs to be without making the list hard to read.
                      </p>
                      <textarea
                        className="vav-session-input"
                        style={{ minHeight: 90, resize: 'vertical', lineHeight: 1.5,
                                 fontFamily: "'Source Sans 3', sans-serif" }}
                        placeholder="A 60-minute session where we…"
                        value={s.description || ""}
                        onChange={e => updateSession(s.id, "description", e.target.value)}
                        data-testid={`session-description-${s.id}`}
                      />
                    </div>

                    {/* External booking link for THIS session — she keeps one link per offering */}
                    {modes.external && (
                      <div className="vav-session-field">
                        <label className="vav-session-label">
                          External booking link for this session (optional)
                        </label>
                        <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 4px' }}>
                          Paste this session's own link on {externalAgent || 'your booking platform'}.
                          Members see this session in your list here and go straight to that page to
                          book and pay — so it is <strong>not</strong> booked or paid for on 11/11.
                          Leave it empty to take bookings for this session in the app.
                        </p>
                        <input
                          className="vav-session-input"
                          style={{ width: '100%' }}
                          placeholder={`https://${(externalAgent || 'calendly').toLowerCase().replace(/[^a-z0-9]/g, '')}.com/your-name/reiki-session`}
                          value={s.external_url || ""}
                          onChange={e => updateSession(s.id, "external_url", e.target.value)}
                          data-testid={`session-external-url-${s.id}`}
                        />
                      </div>
                    )}

                    {/* Row 3: Healing Duration + Before Buffer + After Buffer */}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <div className="vav-session-field" style={{ flex: 1, minWidth: 130 }}>
                        <label className="vav-session-label">Time for Healing</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            className="vav-session-input"
                            type="number"
                            min="5"
                            max="480"
                            step="5"
                            value={s.healing_duration || s.duration_minutes || 60}
                            onChange={e => updateSession(s.id, "healing_duration", parseInt(e.target.value) || 60)}
                            data-testid={`session-healing-${s.id}`}
                            style={{ width: 80 }}
                          />
                          <span style={{ fontSize: 13, color: 'var(--ink-mid)' }}>min</span>
                        </div>
                      </div>
                      <div className="vav-session-field" style={{ flex: 1, minWidth: 130 }}>
                        <label className="vav-session-label">Before Session Lag</label>
                        <select
                          className="vav-session-input"
                          value={s.before_buffer || 0}
                          onChange={e => updateSession(s.id, "before_buffer", parseInt(e.target.value))}
                          data-testid={`session-before-${s.id}`}
                          style={{ appearance: "none", cursor: "pointer", width: "100%" }}
                        >
                          {[0, 5, 10, 15, 20, 30, 45, 60].map(d => <option key={d} value={d}>{d === 0 ? 'None' : `${d} min`}</option>)}
                        </select>
                      </div>
                      <div className="vav-session-field" style={{ flex: 1, minWidth: 130 }}>
                        <label className="vav-session-label">After Session Lag</label>
                        <select
                          className="vav-session-input"
                          value={s.after_buffer || 0}
                          onChange={e => updateSession(s.id, "after_buffer", parseInt(e.target.value))}
                          data-testid={`session-after-${s.id}`}
                          style={{ appearance: "none", cursor: "pointer", width: "100%" }}
                        >
                          {[0, 5, 10, 15, 20, 30, 45, 60].map(d => <option key={d} value={d}>{d === 0 ? 'None' : `${d} min`}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Row 3: Total duration callout */}
                    <div style={{
                      background: 'var(--teal-bg)',
                      border: '1px solid var(--teal-border)',
                      borderRadius: 8,
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: 'var(--teal)',
                    }}>
                      <Clock size={14} />
                      <span>
                        Total time allotted: <strong>{(s.before_buffer || 0) + (s.healing_duration || s.duration_minutes || 60) + (s.after_buffer || 0)} min</strong>
                        {(s.before_buffer > 0 || s.after_buffer > 0) && (
                          <span style={{ color: 'var(--ink-mid)', fontWeight: 400 }}>
                            {' '}({s.before_buffer || 0}m prep + {s.healing_duration || s.duration_minutes || 60}m healing + {s.after_buffer || 0}m wind-down)
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Row 4: Hard Start Time + Only Session toggle */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div className="vav-session-field" style={{ flex: 1, minWidth: 180 }}>
                        <label className="vav-session-label">Hard Start Time (optional)</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            className="vav-session-input"
                            type="time"
                            value={s.hard_start_time || ""}
                            onChange={e => updateSession(s.id, "hard_start_time", e.target.value || null)}
                            data-testid={`session-hard-start-${s.id}`}
                            style={{ width: 130 }}
                          />
                          {s.hard_start_time && (
                            <button
                              onClick={() => updateSession(s.id, "hard_start_time", null)}
                              style={{ background: 'none', border: 'none', color: 'var(--ink-light)', cursor: 'pointer', padding: 2 }}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                        <p style={{ fontSize: 11, color: 'var(--ink-light)', marginTop: 4 }}>
                          {s.hard_start_time ? `Session always starts at ${s.hard_start_time}` : 'Fits into your weekly schedule'}
                        </p>
                      </div>

                      <div className="vav-session-field" style={{ flex: 1, minWidth: 180 }}>
                        <label className="vav-session-label">Only session for the day</label>
                        <div
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                            padding: '8px 12px', borderRadius: 8, marginTop: 2,
                            background: s.only_session_for_day ? 'var(--amber-bg)' : 'var(--off-white)',
                            border: `1px solid ${s.only_session_for_day ? '#d9b34a' : 'var(--border)'}`,
                          }}
                          onClick={() => updateSession(s.id, "only_session_for_day", !s.only_session_for_day)}
                          data-testid={`session-only-day-${s.id}`}
                        >
                          {s.only_session_for_day
                            ? <ToggleRight size={20} color="var(--amber)" />
                            : <ToggleLeft size={20} color="var(--ink-light)" />
                          }
                          <span style={{ fontSize: 13, color: s.only_session_for_day ? 'var(--amber)' : 'var(--ink-mid)' }}>
                            {s.only_session_for_day ? 'Yes — blocks entire day when booked' : 'No — other sessions allowed'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Package offer */}
                    <div style={{
                      border: `1px solid ${s.offer_as_package ? 'var(--teal-border)' : 'var(--border)'}`,
                      borderRadius: 10,
                      padding: 14,
                      background: s.offer_as_package ? 'var(--teal-bg)' : 'var(--off-white)',
                      transition: 'all 0.2s',
                    }}>
                      <div
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                        }}
                        onClick={() => updateSession(s.id, "offer_as_package", !s.offer_as_package)}
                        data-testid={`session-package-toggle-${s.id}`}
                      >
                        {s.offer_as_package
                          ? <ToggleRight size={20} color="var(--teal)" />
                          : <ToggleLeft size={20} color="var(--ink-light)" />
                        }
                        <span style={{ fontSize: 13, fontWeight: 600, color: s.offer_as_package ? 'var(--teal)' : 'var(--ink-mid)' }}>
                          Offer as session package
                        </span>
                      </div>

                      {s.offer_as_package && (
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                          <div className="vav-session-field" style={{ minWidth: 100 }}>
                            <label className="vav-session-label">Sessions in package</label>
                            <input
                              className="vav-session-input"
                              type="number"
                              min="2"
                              max="20"
                              value={s.package_size || 4}
                              onChange={e => updateSession(s.id, "package_size", parseInt(e.target.value) || 4)}
                              data-testid={`session-pkg-size-${s.id}`}
                              style={{ width: 80 }}
                            />
                          </div>
                          <div className="vav-session-field" style={{ minWidth: 120 }}>
                            <label className="vav-session-label">Package price (USD)</label>
                            <input
                              className="vav-session-input"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder={(s.price || 0) * (s.package_size || 4)}
                              value={s.package_price || ""}
                              onChange={e => updateSession(s.id, "package_price", parseFloat(e.target.value) || 0)}
                              data-testid={`session-pkg-price-${s.id}`}
                              style={{ width: 110 }}
                            />
                          </div>
                          <div className="vav-session-field" style={{ minWidth: 100 }}>
                            <label className="vav-session-label">Expires after (days)</label>
                            <input
                              className="vav-session-input"
                              type="number"
                              min="7"
                              max="365"
                              value={s.package_expiry_days || 90}
                              onChange={e => updateSession(s.id, "package_expiry_days", parseInt(e.target.value) || 90)}
                              data-testid={`session-pkg-expiry-${s.id}`}
                              style={{ width: 80 }}
                            />
                          </div>
                          {s.price > 0 && (s.package_price || 0) > 0 && (
                            <div style={{
                              display: 'flex', alignItems: 'flex-end', fontSize: 12, color: 'var(--teal)', fontWeight: 600, paddingBottom: 6,
                            }}>
                              {(() => {
                                const fullPrice = s.price * (s.package_size || 4);
                                const savings = fullPrice - (s.package_price || fullPrice);
                                const pct = ((savings / fullPrice) * 100).toFixed(0);
                                return savings > 0 ? `Save $${savings.toFixed(2)} (${pct}% off)` : '';
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                      {s.offer_as_package && sessionErrors[s.id] && (
                        <p style={{ fontSize: 12, color: 'var(--red)', margin: '8px 0 0' }}
                           data-testid={`session-pkg-error-${s.id}`}>
                          {sessionErrors[s.id]}
                        </p>
                      )}
                      {s.offer_as_package && s.price > 0 && (s.package_price || 0) > 0
                        && s.package_price >= s.price * (s.package_size || 4) && (
                        <p style={{ fontSize: 12, color: '#8a6a0a', margin: '8px 0 0' }}
                           data-testid={`session-pkg-no-saving-${s.id}`}>
                          Heads up — this package costs {s.package_price === s.price * (s.package_size || 4)
                            ? 'the same as' : 'more than'} booking {s.package_size || 4} sessions one
                          at a time (${(s.price * (s.package_size || 4)).toFixed(2)}). Your call, but
                          clients will see the comparison.
                        </p>
                      )}
                    </div>

                    {/* Row 6: Where it happens */}
                    <div className="vav-session-field">
                      <label className="vav-session-label">Where does this session happen?</label>
                      <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 6px' }}>
                        Leave the link blank if you open a new meeting room for each session — you paste
                        that link on the booking itself when you confirm, and your client always sees
                        the current one.
                      </p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {[
                          { id: 'video',     label: 'Video call' },
                          { id: 'in_person', label: 'In person' },
                          { id: 'phone',     label: 'Phone call' },
                          { id: 'custom',    label: 'Something else' },
                        ].map(opt => (
                          <button
                            key={opt.id}
                            onClick={() => updateSession(s.id, "location_mode", opt.id)}
                            data-testid={`session-location-mode-${opt.id}-${s.id}`}
                            style={{
                              padding: '6px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                              fontFamily: "'Source Sans 3', sans-serif",
                              border: `1px solid ${(s.location_mode || 'video') === opt.id ? 'var(--teal)' : 'var(--border)'}`,
                              background: (s.location_mode || 'video') === opt.id ? 'var(--teal-bg)' : 'transparent',
                              color: (s.location_mode || 'video') === opt.id ? 'var(--teal)' : 'var(--ink-mid)',
                              fontWeight: (s.location_mode || 'video') === opt.id ? 600 : 400,
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {(s.location_mode || 'video') === 'video' && (
                        <input
                          className="vav-session-input"
                          placeholder="Standing meeting link (optional) — e.g. your permanent Zoom room"
                          value={s.location_link || ""}
                          onChange={e => updateSession(s.id, "location_link", e.target.value)}
                          data-testid={`session-location-link-${s.id}`}
                        />
                      )}
                      {(s.location_mode || 'video') === 'in_person' && (
                        <textarea
                          className="vav-session-input"
                          style={{ minHeight: 64, resize: 'vertical', lineHeight: 1.5, fontFamily: "'Source Sans 3', sans-serif" }}
                          placeholder="Street address, suite, city, state, zip"
                          value={s.location_address || ""}
                          onChange={e => updateSession(s.id, "location_address", e.target.value)}
                          data-testid={`session-location-address-${s.id}`}
                        />
                      )}
                      <textarea
                        className="vav-session-input"
                        style={{ minHeight: 56, resize: 'vertical', lineHeight: 1.5, marginTop: 6, fontFamily: "'Source Sans 3', sans-serif" }}
                        placeholder={
                          (s.location_mode || 'video') === 'phone'
                            ? "e.g. I'll call the number on your booking at your start time"
                            : "Anything else they need — parking, which door, what to bring (optional)"
                        }
                        value={s.location_note || ""}
                        onChange={e => updateSession(s.id, "location_note", e.target.value)}
                        data-testid={`session-location-note-${s.id}`}
                      />
                    </div>

                    {/* Row 7: Intake forms + Intro email */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div className="vav-session-field">
                        <label className="vav-session-label">Intake forms (optional)</label>
                        <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 4px' }}>
                          Upload a PDF or link an online form, and give it a name your client will
                          recognise. Sent with their confirmation and kept on their booking.
                        </p>
                        <IntakeFormsEditor
                          forms={s.intake_forms || []}
                          onChange={forms => updateSession(s.id, 'intake_forms', forms)}
                          testPrefix={`session-intake-${s.id}`}
                          apiFetch={apiFetch}
                        />
                      </div>
                      <div className="vav-session-field">
                        <label className="vav-session-label">Introduction email message (optional)</label>
                        <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 4px' }}>
                          Sent with the confirmation email for THIS offering only, and it replaces
                          your default confirmed-email message from the Client Emails tab. Use it for
                          preparation instructions, what to expect, or a personal welcome.
                        </p>
                        <textarea
                          className="vav-session-input"
                          style={{ minHeight: 120, resize: 'vertical', lineHeight: 1.5, fontFamily: "'Source Sans 3', sans-serif" }}
                          placeholder="Welcome to your healing session! Here's what to expect..."
                          value={s.intro_email_text || ""}
                          onChange={e => updateSession(s.id, "intro_email_text", e.target.value)}
                          data-testid={`session-intro-email-${s.id}`}
                        />
                      </div>
                      <div className="vav-session-field">
                        <label className="vav-session-label">
                          Follow-up emails after the session (optional)
                        </label>
                        <SessionFollowupsEditor
                          followups={s.followups || []}
                          onChange={items => updateSession(s.id, 'followups', items)}
                          testPrefix={`session-${s.id}`}
                        />
                      </div>
                    </div>

                    {/* Save right here — no scrolling back to the top before leaving the page */}
                    {!s.offer_as_package && sessionErrors[s.id] && (
                      <p style={{ fontSize: 12, color: '#a3372f', background: 'rgba(163,55,47,0.07)',
                                  border: '1px solid rgba(163,55,47,0.25)', borderRadius: 6,
                                  padding: '8px 10px', margin: 0 }}
                         data-testid={`session-save-error-${s.id}`}>
                        {sessionErrors[s.id]}
                      </p>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {savedSession === s.id ? (
                        <div className="vav-saved-pill" data-testid={`session-saved-${s.id}`}>
                          <Check size={14} /> Saved
                        </div>
                      ) : (
                        <button
                          className="vav-save-btn"
                          onClick={() => save({ sessionId: s.id })}
                          disabled={saving}
                          data-testid={`session-save-${s.id}`}
                        >
                          {saving
                            ? <><Loader size={14} style={{ animation: "spin 1s linear infinite" }} /> Saving...</>
                            : <><Save size={14} /> Save this offering</>}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <button className="vav-add-session-btn" onClick={addSession} data-testid="add-session-type-btn">
                  <Plus size={15} /> Add session type
                </button>
              </div>
            </div>
          )}

          {/* ── Blocked dates tab ── */}
          {tab === "blocked" && (
            <div className="vav-section">
              <p className="vav-section-title">Blocked dates</p>
              <p className="vav-section-desc">Mark days or ranges when you're unavailable — holidays, retreats, vacation. No bookings will be accepted on these dates.</p>

              <div className="vav-blocked-list">
                {blockedDates.map(b => (
                  <div key={b.id} className="vav-blocked-row">
                    <Calendar size={15} color="var(--ink-light)" />
                    <div style={{ flex: 1 }}>
                      <div className="vav-blocked-dates">
                        {b.date_start === b.date_end ? b.date_start : `${b.date_start} → ${b.date_end}`}
                      </div>
                      {b.reason && <div className="vav-blocked-reason">{b.reason}</div>}
                    </div>
                    <button className="vav-blocked-delete" onClick={() => removeBlockedDate(b.id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="vav-add-blocked">
                <div className="vav-input-group">
                  <label className="vav-input-label">From</label>
                  <input
                    className="vav-date-input"
                    type="date"
                    value={newBlock.date_start}
                    onChange={e => setNewBlock(b => ({ ...b, date_start: e.target.value }))}
                  />
                </div>
                <div className="vav-input-group">
                  <label className="vav-input-label">To</label>
                  <input
                    className="vav-date-input"
                    type="date"
                    value={newBlock.date_end}
                    onChange={e => setNewBlock(b => ({ ...b, date_end: e.target.value }))}
                  />
                </div>
                <div className="vav-input-group">
                  <label className="vav-input-label">Reason (optional)</label>
                  <input
                    className="vav-date-input"
                    placeholder="e.g. Vacation, retreat..."
                    value={newBlock.reason}
                    onChange={e => setNewBlock(b => ({ ...b, reason: e.target.value }))}
                  />
                </div>
                <button className="vav-add-blocked-btn" onClick={addBlockedDate}>
                  Block dates
                </button>
              </div>
            </div>
          )}

          {/* ── Booking modes tab ── */}
          {tab === "modes" && (
            <div className="vav-section">
              <p className="vav-section-title">Booking modes</p>
              <p className="vav-section-desc">Choose how clients can book with you. You can enable multiple modes simultaneously.</p>

              <div className="vav-mode-cards">
                {/* Mode 1 */}
                <div className={`vav-mode-card ${modes.in_app ? "active" : ""}`}>
                  <div className="vav-mode-card-head">
                    <div>
                      <p className="vav-mode-title">In-app booking</p>
                      <p className="vav-mode-desc">Silver, Gold, Platinum, and Free-Comp members can book directly through the 11/11 Collective Connection app. Payment is held and captured when you confirm.</p>
                    </div>
                    <DayToggle on={modes.in_app} onToggle={() => setModes(m => ({ ...m, in_app: !m.in_app }))} />
                  </div>
                </div>

                {/* Mode 2 */}
                <div className={`vav-mode-card ${modes.public ? "active" : ""}`}>
                  <div className="vav-mode-card-head">
                    <div>
                      <p className="vav-mode-title">Public booking page</p>
                      <p className="vav-mode-desc">Anyone on the web can book without an 11/11 Collective Connection account. A public page is created at your booking URL — embeddable on any site as an iframe.</p>
                    </div>
                    <DayToggle on={modes.public} onToggle={() => setModes(m => ({ ...m, public: !m.public }))} />
                  </div>
                  {modes.public && (
                    <div className="vav-mode-extra">
                      {publishedPublic ? (
                        <>
                          <div className="vav-public-link">
                            <ExternalLink size={13} />
                            <a href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a>
                            <button
                              type="button"
                              className="vav-copy-btn"
                              onClick={() => copyToClipboard(publicUrl, "link")}
                              data-testid="copy-public-booking-url"
                            >
                              {copied === "link" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                            </button>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--ink-light)" }}>
                            Paste it into an email, a newsletter or a social profile — anyone can book
                            from it without an account.
                          </div>
                          <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              Embed on any site:
                              <button
                                type="button"
                                className="vav-copy-btn"
                                onClick={() => copyToClipboard(embedCode, "embed")}
                                data-testid="copy-public-booking-embed"
                              >
                                {copied === "embed" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy code</>}
                              </button>
                            </div>
                            <code style={{ display: "block", background: "var(--off-white)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", marginTop: 6, fontSize: 11, lineHeight: 1.6 }}>
                              {embedCode}
                            </code>
                          </div>
                        </>
                      ) : (
                        // The URL used to be shown the instant the toggle moved, so a save that
                        // was refused left him holding a link that answered "Booking page not
                        // found". No address until the page is actually live.
                        <div style={{ fontSize: 12, color: "var(--ink-light)" }}
                             data-testid="public-page-not-published-yet">
                          Hit <strong>Save</strong> to publish your page — your shareable link and
                          embed code appear here once it is live.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Mode 3 */}
                <div className={`vav-mode-card ${modes.external ? "active" : ""}`}>
                  <div className="vav-mode-card-head">
                    <div>
                      <p className="vav-mode-title">External booking agent</p>
                      <p className="vav-mode-desc">Already using Calendly, Acuity, or another platform? Link it here. App users will be taken to your external page in an overlay that keeps 11/11 Collective Connection active.</p>
                    </div>
                    <DayToggle on={modes.external} onToggle={() => setModes(m => ({ ...m, external: !m.external }))} />
                  </div>
                  {modes.external && (
                    <div className="vav-mode-extra">
                      <div className="vav-input-group">
                        <label className="vav-input-label">Booking platform (e.g. Calendly, Acuity, TidyCal)</label>
                        <input
                          className="vav-url-input"
                          placeholder="Calendly"
                          value={externalAgent}
                          onChange={e => setExternalAgent(e.target.value)}
                        />
                      </div>
                      <div className="vav-input-group">
                        <label className="vav-input-label">Your booking URL</label>
                        <input
                          className="vav-url-input"
                          placeholder="https://calendly.com/your-name"
                          value={externalUrl}
                          onChange={e => setExternalUrl(e.target.value)}
                          data-testid="external-url-input"
                        />
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-light)", lineHeight: 1.6 }}>
                        Keep a <strong>separate link for each thing you offer</strong>? Add your
                        session types under <strong>Session Types</strong> and paste each one's link on
                        the session itself — members then pick the session they want and go straight
                        to the right page. The URL above stays as your general booking page.
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-light)", lineHeight: 1.6 }}>
                        <strong>Optional — bringing clients back here afterwards.</strong> In
                        Calendly, Acuity and most others this redirect is set <em>per event type</em>{" "}
                        and applies to every booking of that type, wherever it was made — so if you
                        also take bookings from your own website using the same event type, those
                        clients would be sent here too, which may confuse someone who has never
                        heard of us. Two safe ways: duplicate the event type and use the copy only
                        for the link above, setting that copy's redirect to{" "}
                        <code style={{ background: "var(--off-white)", padding: "1px 5px", borderRadius: 4 }}>
                          {`${window.location.origin}/booking-complete`}
                        </code>{" "}
                        — or just skip the redirect entirely. Members who book from inside 11/11
                        never leave: your booking page opens over the top of it.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Tip & Donation Links tab ── */}
          {tab === "payment" && (
            <ExternalPaymentSettings />
          )}

          {/* ── Cancellation Policy tab ── */}
          {tab === "cancellation" && (
            <CancellationPolicyEditor policy={policy} setPolicy={setPolicy} />
          )}

          {tab === "limits" && (
            <SchedulingLimitsPanel profile={loadedProfile} onSaved={setLoadedProfile} />
          )}

          {tab === "emails" && (
            <ClientEmailsPanel profile={loadedProfile} onSaved={setLoadedProfile} />
          )}

        </div>
      </div>
    </>
  );
}
